import {
  capienzaEvento,
  countEventOccupiedSeats,
  countVariantOccupiedSeats,
} from './guestEventEligibility';

export { capienzaEvento };

/**
 * Calcola i posti disponibili.
 * Se capienza non è definita, restituisce null.
 */
export const getAvailableSeats = (event) => {
  const eventCapacity = capienzaEvento(event);
  if (!eventCapacity) return null;

  return eventCapacity - countEventOccupiedSeats(event);
};

export const filterFutureObjects = (objects = []) => {
  const now = new Date();
  return objects?.filter((obj) => {
    const endDate = obj.Data_Fine ?? obj.data_fine;
    const startDate = obj.Data_inizio ?? obj.data_inizio;
    if (endDate) {
      const end = new Date(endDate);
      if (!Number.isNaN(end.getTime())) return end >= now;
    }
    if (startDate) {
      const start = new Date(startDate);
      if (!Number.isNaN(start.getTime())) return start >= now;
    }
    // Nessuna data valida: include l'oggetto (es. varianti settimane centro estivo)
    return true;
  });
};

/**
 * Ordina una lista di varianti/eventi per data di inizio (e, in fallback, per nome).
 * Usa Data_inizio se presente, altrimenti Data o Data_Fine.
 * Mantiene gli oggetti senza data alla fine, ordinati per nome.
 */
/**
 * Prezzo pieno di una variante: prezzoVariante se impostato (0 = gratis),
 * altrimenti il prezzo dell'evento (vuoto = eredita).
 */
export const getVariantPrice = (variant, event) => {
  const own = variant?.prezzoVariante;
  if (own !== null && own !== undefined && own !== '') return Number(own) || 0;
  return Number(event?.prezzo) || 0;
};

/** Con varianti l'evento è gratuito solo se lo sono tutte; senza varianti vale is_free. */
export const isEventEffectivelyFree = (event) => {
  const varianti = event?.Varianti || [];
  if (!varianti.length) return Boolean(event?.is_free);
  return varianti.every((v) => getVariantPrice(v, event) === 0);
};

export const sortVariantiByDate = (varianti = []) => {
  return [...varianti].sort((a, b) => {
    const getDate = (v) => v?.Data_inizio || v?.Data || v?.Data_Fine || null;

    const dateA = getDate(a);
    const dateB = getDate(b);

    const tsA = dateA ? new Date(dateA).getTime() : Infinity;
    const tsB = dateB ? new Date(dateB).getTime() : Infinity;

    if (tsA !== tsB) {
      return tsA - tsB;
    }

    const nameA = (a?.Nome || '').toString();
    const nameB = (b?.Nome || '').toString();

    return nameA.localeCompare(nameB, 'it', {
      numeric: true,
      sensitivity: 'base',
    });
  });
};

export function capitalizeFirstLetter(str) {
  if (typeof str !== 'string' || str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Formatta data abbreviata (GG MeseAbbr).
 * Esempio output: "6 Ago"
 */
export const formatDateAbbr = (
  dateString,
  { withTime, withYear = true } = {}
) => {
  const date = new Date(dateString);
  const monthAbbr = [
    'Gen',
    'Feb',
    'Mar',
    'Apr',
    'Mag',
    'Giu',
    'Lug',
    'Ago',
    'Set',
    'Ott',
    'Nov',
    'Dic',
  ];

  const day = date.getDate();
  const month = monthAbbr[date.getMonth()];
  const year = date.getFullYear();

  const formattedDate = `${day} ${month}${withYear ? ` ${year}` : ''}`;

  if (withTime) {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    // Pad hours and minutes with leading zeros if necessary
    hours = hours < 10 ? `0${hours}` : hours;
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${formattedDate}, ${hours}:${minutes}`;
  }

  return formattedDate;
};

/**
 * Restituisce testo, bg, color e eventuale animazione
 * in base alla percentuale di posti disponibili
 */
export const getButtonProps = (event, availableSeats) => {
  // Controllo prioritario: se l'evento ha varianti, mostra sempre "AAAA"
  if (event?.Varianti?.length > 0) {
    return {
      text: 'Scopri le opzioni!',
      bg: '#ffcc00',
      color: 'black',
      sx: {},
      flexShrink: 0,
    };
  }

  // Se la capienza non è definita o i posti disponibili sono nulli
  const eventCapacity =
    event.capienza || event.Capienza || event.Max_Participants;
  if (!eventCapacity || availableSeats == null) {
    return {
      text: 'Scopri i dettagli!',
      bg: '#ffcc00',
      color: 'black',
      sx: {},
    };
  }

  const perc = (availableSeats / eventCapacity) * 100;

  if (perc <= 0) {
    return {
      text: 'Posti Esauriti!',
      bg: 'red.500',
      color: 'black',
      sx: {},
    };
  }
  if (perc >= 50) {
    return {
      text: 'Scopri i dettagli!',
      bg: '#ffcc00',
      color: 'black',
      sx: {},
    };
  }
  if (perc >= 21) {
    return {
      text: 'I posti stanno per finire!',
      bg: 'orange.400',
      color: 'white',
      sx: {},
    };
  }

  return {
    text: 'Ultimi posti!',
    bg: 'red.500',
    color: 'white',
    sx: {
      '@keyframes pulse': {
        '0%': { transform: 'scale(1)' },
        '50%': { transform: 'scale(1.05)' },
        '100%': { transform: 'scale(1)' },
      },
      animation: 'pulse 1.5s infinite ease-in-out',
    },
  };
};

/**
 * Determina il colore del badge (partecipanti) come in Home
 */
export const getBadgeColor = (partecipanti) => {
  switch (partecipanti) {
    case 'Per Ragazzi':
      return 'blue.400';
    case 'Per Adulti':
      return 'purple.400';
    case 'Per Tutti':
      return 'green.400';
    default:
      return 'gray.400';
  }
};

/**
 * Formatta data completa (GG Mese Anno).
 * Se withTime è true, aggiunge anche l'orario (HH:MM).
 * Esempio output: "6 Agosto 2023" oppure "6 Agosto 2023, 14:05"
 */
export const formatDateFull = (dateString, { withTime, withYear } = {}) => {
  const date = new Date(dateString);
  const monthNames = [
    'Gennaio',
    'Febbraio',
    'Marzo',
    'Aprile',
    'Maggio',
    'Giugno',
    'Luglio',
    'Agosto',
    'Settembre',
    'Ottobre',
    'Novembre',
    'Dicembre',
  ];

  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const formattedDate = `${day} ${month} ${withYear ? ` ${year}` : ''}`;

  if (withTime) {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    // Pad hours and minutes with leading zeros if necessary
    hours = hours < 10 ? `0${hours}` : hours;
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${formattedDate}, ${hours}:${minutes}`;
  }

  return formattedDate;
};
/**
 * Data e orario di una variante (Data_inizio / Data_Fine sono datetime).
 * Esempio: { date: "7 Lug – 11 Lug", time: "08:00–16:30" }; time è null se manca l'ora.
 */
export const formatVariantSchedule = (variant) => {
  if (!variant?.Data_inizio) return { date: null, time: null };
  const start = new Date(variant.Data_inizio);
  const end = variant.Data_Fine ? new Date(variant.Data_Fine) : null;
  const hhmm = (d) =>
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

  const startDate = formatDateAbbr(start, { withYear: false });
  const sameDay = !end || start.toDateString() === end.toDateString();
  const date = sameDay
    ? startDate
    : `${startDate} – ${formatDateAbbr(end, { withYear: false })}`;

  // Mezzanotte = variante salvata senza ora
  const hasTime = start.getHours() !== 0 || start.getMinutes() !== 0;
  if (!hasTime) return { date, time: null };
  return { date, time: end ? `${hhmm(start)}–${hhmm(end)}` : hhmm(start) };
};

export const smoothScrollTo = (targetPosition, duration = 600) => {
  const startPosition = window.scrollY;
  const distance = targetPosition - startPosition;
  let startTime = null;

  function easeInOutQuad(t, b, c, d) {
    // eslint-disable-next-line no-param-reassign
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    // eslint-disable-next-line no-plusplus, no-param-reassign
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  }

  function animation(currentTime) {
    if (!startTime) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const run = easeInOutQuad(timeElapsed, startPosition, distance, duration);
    window.scrollTo(0, run);
    if (timeElapsed < duration) requestAnimationFrame(animation);
  }

  requestAnimationFrame(animation);
};

export function getNextOccurrence(recurringDay) {
  // Map Italian weekday names to numbers (JavaScript: Sunday = 0, Monday = 1, …)
  const dayMapping = {
    Domenica: 0,
    Lunedì: 1,
    Martedì: 2,
    Mercoledì: 3,
    Giovedì: 4,
    Venerdì: 5,
    Sabato: 6,
  };

  const today = new Date();
  const todayDay = today.getDay();
  const targetDay = dayMapping[recurringDay];

  // Calculate days until next occurrence.
  // If the recurring day is today, daysToAdd will be 0.
  // (If you want to check the event's time to decide if it already passed today,
  // add extra logic here.)
  const daysToAdd = (targetDay - todayDay + 7) % 7;

  const nextOccurrence = new Date(today);
  nextOccurrence.setDate(today.getDate() + daysToAdd);
  return nextOccurrence;
}

/**
 * Standardizes error objects from API calls (e.g., via Axios) or native errors.
 *
 * @param {any} error - The error object caught from a request.
 * @returns {object} An object containing at least a 'message' property,
 *                   optionally including status and additional data.
 */
export function standardizeError(error) {
  if (error.response) {
    // Error received from the server.
    const responseData = error.response.data;
    const message =
      (responseData && (responseData.error || responseData.message)) ||
      error.message ||
      'Errore interno del server';
    return {
      message,
      status: error.response.status,
      data: responseData,
    };
  }
  if (error.request) {
    // Request was made but no response received.
    return {
      message: 'Nessuna risposta dal server.',
      data: error.request,
    };
  }
  if (error instanceof Error) {
    // Generic JS error.
    return {
      message: error.message,
    };
  }
  // Fallback for unknown error types.
  return {
    message: String(error),
  };
}

export const getRecurringStartAndEndDate = (targetDay) => {
  const today = new Date();
  const diff = (today.getDay() + 7 - targetDay) % 7;
  const lastOccurrence = new Date(today);
  lastOccurrence.setDate(today.getDate() - diff);
  // If today is the recurring day, take the previous occurrence
  if (today.getDay() === targetDay) {
    lastOccurrence.setDate(lastOccurrence.getDate() - 7);
  }

  // Start from the day after the last occurrence at 00:00:00
  const startDate = new Date(lastOccurrence);
  startDate.setDate(startDate.getDate() + 1);
  startDate.setHours(0, 0, 0, 0);

  // End at the next occurrence's day at 23:59:59.999
  const endDate = new Date(lastOccurrence);
  endDate.setDate(endDate.getDate() + 7);
  endDate.setHours(23, 59, 59, 999);

  return {
    startDate,
    endDate,
  };
};

/**
 * Calcola il range di prezzo per eventi con varianti.
 * Restituisce il prezzo singolo se non ci sono varianti o se tutte le varianti hanno lo stesso prezzo.
 * Restituisce il range (min-max) se le varianti hanno prezzi diversi.
 */
export const getEventPriceRange = (event) => {
  // Se l'evento è gratuito, restituisci sempre "Gratis"
  if (event.is_free) {
    return 'Gratis';
  }

  // Se non ci sono varianti, usa il prezzo dell'evento
  if (!event.Varianti || event.Varianti.length === 0) {
    return event.prezzo ? `€${event.prezzo}` : 'Prezzo da definire';
  }

  // Raccogli tutti i prezzi delle varianti
  const prices = event.Varianti.map(
    (variant) => variant.prezzoVariante || event.prezzo
  ).filter((price) => price !== null && price !== undefined);

  // Se non ci sono prezzi validi
  if (prices.length === 0) {
    return 'Prezzo da definire';
  }

  // Se tutti i prezzi sono uguali, mostra solo quel prezzo
  const uniquePrices = [...new Set(prices)];
  if (uniquePrices.length === 1) {
    return `€${uniquePrices[0]}`;
  }

  // Se ci sono prezzi diversi, mostra il range
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  return `€${minPrice} - €${maxPrice}`;
};

/**
 * Helper per calcolare posti disponibili per una variante.
 * Replica la logica di EventDetail.js
 * Funziona anche quando gli ordini non hanno il campo 'socio' popolato
 */
// Soci unici più partecipanti ospite; 999 se la variante non ha capienza
export const calculateVariantAvailableSeats = (variant) => {
  if (!variant?.Capienza) return 999;
  return variant.Capienza - countVariantOccupiedSeats(variant);
};

/**
 * Determina se un evento è sold out usando la stessa logica di EventDetail.js.
 * - Per eventi senza varianti: controlla se availableSeats <= 0
 * - Per eventi con varianti: controlla se almeno una variante ha posti disponibili
 *
 * Nota: Funziona anche quando gli ordini non hanno il campo 'socio' popolato (come nella lista eventi)
 */
export const isSoldOut = (event) => {
  // Se l'evento ha varianti, controlla se almeno una variante ha posti disponibili
  if (event?.Varianti?.length > 0) {
    const hasAvailableVariants = event.Varianti.some((variant) => {
      const availableSeats = calculateVariantAvailableSeats(variant);
      return availableSeats > 0;
    });
    return !hasAvailableVariants;
  }

  // Per eventi senza varianti
  const eventCapacity = capienzaEvento(event);

  // Se non c'è capienza definita, non è sold out
  if (!eventCapacity) {
    return false;
  }

  const availableSeats = eventCapacity - countEventOccupiedSeats(event);
  return availableSeats <= 0;
};

/**
 * Determina se un socio è coinvolto in un evento (ha un ordine per quell'evento).
 * Replica la logica usata in EventDetail.js per verificare se un utente ha già acquistato.
 *
 * @param {Object} event - L'evento da controllare
 * @param {Object} userData - I dati dell'utente contenenti gli ordini
 * @returns {boolean} True se il socio è coinvolto nell'evento
 */
export const isSocioInvolvedInEvent = (event, userData) => {
  if (
    !event ||
    !userData ||
    !userData.orders ||
    !Array.isArray(userData.orders)
  ) {
    return false;
  }

  const eventId = event.documentId || event.id;
  if (!eventId) return false;

  // Filtra gli ordini per questo evento
  const eventOrders = userData.orders.filter((order) => {
    // Controlla se l'ordine è per questo evento
    const orderEventId = order.event?.documentId || order.event?.id;
    if (orderEventId !== eventId) return false;

    // Escludi ordini cancellati
    const isCancelled = order.metadata?.products?.some(
      (product) => product.isCancelled === true
    );
    if (isCancelled) return false;

    // Se l'evento ha varianti, controlla se l'ordine contiene almeno una variante dell'evento
    if (event.Varianti && event.Varianti.length > 0) {
      const variantIds = event.Varianti.map((v) => v.documentId || v.id);
      const orderHasVariant = order.metadata?.products?.some((product) => {
        const productId = product.id;
        return (
          variantIds.includes(productId) &&
          ['acconto', 'totale'].includes(product.paymentType)
        );
      });
      return orderHasVariant;
    }

    // Per eventi senza varianti, controlla se l'ordine ha prodotti validi
    const hasValidProduct = order.metadata?.products?.some((product) =>
      ['acconto', 'totale'].includes(product.paymentType)
    );
    return hasValidProduct;
  });

  return eventOrders.length > 0;
};

/**
 * Calcola giorni, ore, minuti e secondi mancanti fino a targetMs.
 * @param {number} nowMs - Timestamp corrente (ms)
 * @param {number} targetMs - Timestamp obiettivo (ms)
 * @returns {{ days: number, hours: number, minutes: number, seconds: number }}
 */
export const getCountdown = (nowMs, targetMs) => {
  const diff = Math.max(0, targetMs - nowMs);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
};

/**
 * Determina se le iscrizioni sono aperte per l'utente in base a aperturaIscrizioniAt (opzionale), iscrizioniAperte (enum) e sconti early-access.
 * @param {Object} event - Evento con aperturaIscrizioniAt (datetime opzionale), iscrizioniAperte: 'true' | 'false' | 'early-access' (o boolean per retrocompatibilità)
 * @param {Object} userData - Dati utente (opzionale se non loggato)
 * @param {Array} availableDiscounts - Sconti disponibili per l'utente (da /api/user/discounts), ciascuno con { type, eventIds }
 * @returns {{ registrationOpen: boolean, isEarlyAccessOnly: boolean }}
 */
export const getRegistrationOpenState = (
  event,
  userData,
  availableDiscounts = []
) => {
  const opensAt = event?.aperturaIscrizioniAt;
  if (opensAt != null) {
    const opensAtTime = new Date(opensAt).getTime();
    if (Date.now() < opensAtTime) {
      const isEarlyAccessOnly = event?.iscrizioniAperte === 'early-access';
      return {
        registrationOpen: false,
        isEarlyAccessOnly: !!isEarlyAccessOnly,
      };
    }
    // Countdown terminato: iscrizioni considerate aperte (equivalente a iscrizioniAperte true)
    return { registrationOpen: true, isEarlyAccessOnly: false };
  }

  const value = event?.iscrizioniAperte;
  // Retrocompatibilità: boolean
  if (value === false) {
    return { registrationOpen: false, isEarlyAccessOnly: false };
  }
  if (value === true) {
    return { registrationOpen: true, isEarlyAccessOnly: false };
  }
  // String enum
  if (value === 'false') {
    return { registrationOpen: false, isEarlyAccessOnly: false };
  }
  if (value === 'true') {
    return { registrationOpen: true, isEarlyAccessOnly: false };
  }
  if (value === 'early-access') {
    const eventId = event?.documentId || event?.id;
    const hasEarlyAccessForEvent = availableDiscounts.some(
      (d) =>
        d.type === 'early-access' &&
        (d.eventIds?.includes('*') ||
          (eventId && d.eventIds?.includes(eventId)))
    );
    return {
      registrationOpen: hasEarlyAccessForEvent,
      isEarlyAccessOnly: true,
    };
  }
  // Valore mancante o non riconosciuto: default chiuso
  return { registrationOpen: false, isEarlyAccessOnly: false };
};
